/**
 * The count of paid orders no admin has actioned yet, for the sidebar badge.
 *
 * TWO THINGS LIVE HERE ON PURPOSE.
 *
 * It is not in `routes/WorkQueue.tsx` any more because the sidebar is mounted
 * on every screen: importing the hook from the route module pinned that route
 * into the always-loaded graph, so `WorkQueue` could never be code-split out of
 * the first-load bundle no matter what `console-routes.tsx` did.
 *
 * And it asks for exactly what the Work Queue landing page asks for — same key,
 * same request — rather than its own `pageSize=1` copy under
 * `["work-queue","counts"]`. Two keys over one upstream handler is a duplicate
 * TanStack cannot see: opening the console ran the queue's whole open-orders
 * scan twice. Sharing the key makes it one query, and on every other screen it
 * leaves the landing page already in cache.
 */
import { useQuery } from "@tanstack/react-query";

import { WORK_QUEUE_LANDING, workQueueListQuery } from "@/lib/queries";
import type { WorkQueueResponse } from "@/types";

/**
 * The badge's staleness, deliberately longer than the queue page's own. Both
 * observers share one query, and each decides refetching for itself: the page
 * an operator is reading stays live, while merely having the sidebar on screen
 * does not keep re-running the scan behind it.
 */
const BADGE_STALE_TIME_MS = 60_000;

/**
 * Zero for a payload that never arrived, so a failed read renders no badge
 * rather than throwing the nav item that carries it.
 */
export function newOrdersCountFrom(
  data: WorkQueueResponse | undefined,
): number {
  return data?.counts?.new_paid_unactioned ?? 0;
}

/** The query options the badge subscribes with — the landing page's own. */
export function newOrdersCountQuery() {
  return {
    ...workQueueListQuery(WORK_QUEUE_LANDING),
    select: newOrdersCountFrom,
    staleTime: BADGE_STALE_TIME_MS,
  };
}

export function useAdminNewOrdersCount(): number {
  const { data } = useQuery(newOrdersCountQuery());
  return data ?? 0;
}
